/**
 * Slice extraction and coordinate math for the interactive SAM panel — kept
 * out of `PromptCanvas.tsx` so the geometry can be reasoned about (and
 * eventually tested) without a DOM.
 *
 * `extractSlice` intentionally duplicates `SliceCanvas.tsx`'s `drawSlice` loop
 * rather than refactoring that component to expose a reusable extractor: here
 * the output is a plain typed array for SAM, there it is pixels written
 * straight into a canvas's `ImageData` — different enough shapes that sharing
 * one function would mean one of the two grows an awkward parameter. What
 * *is* shared, and must stay in lock-step, is the voxel layout: same
 * `axisFor`/`planeLayout`, same top-is-superior/anterior flip, so a point
 * clicked here lands on the same voxel the main viewer would show at that
 * pixel.
 */
import type { NiftiVolume } from "@/lib/nifti";
import { axisFor, planeLayout, type SliceOrientation } from "@/viewer/SliceCanvas";

/** The square the encoder always sees, regardless of the slice's own size. */
export const SAM_INPUT_SIZE = 1024;

export interface ExtractedSlice {
  width: number;
  height: number;
  /** Row-major, top row first (display orientation), normalized to [0, 1]. */
  gray: Float32Array;
}

export function extractSlice(
  vol: NiftiVolume,
  norm: Float32Array,
  orientation: SliceOrientation,
  index: number,
): ExtractedSlice {
  const { dims } = vol;
  const layout = planeLayout(orientation);
  const uDim = dims[layout.uAxis];
  const vDimAxis = orientation === "axial" ? 1 : 2;
  const vDim = dims[vDimAxis];
  const [nx, ny] = dims;
  const voxIndex = (x: number, y: number, z: number) => x + y * nx + z * nx * ny;
  const gray = new Float32Array(uDim * vDim);
  for (let v = 0; v < vDim; v++) {
    const destRow = vDim - 1 - v;
    for (let u = 0; u < uDim; u++) {
      let x = 0;
      let y = 0;
      let z = 0;
      if (orientation === "axial") {
        x = u;
        y = v;
        z = index;
      } else if (orientation === "coronal") {
        x = u;
        y = index;
        z = v;
      } else {
        x = index;
        y = u;
        z = v;
      }
      gray[destRow * uDim + u] = norm[voxIndex(x, y, z)] ?? 0;
    }
  }
  return { width: uDim, height: vDim, gray };
}

export function sliceDepth(vol: NiftiVolume, orientation: SliceOrientation): number {
  return vol.dims[axisFor(orientation)];
}

/**
 * Grayscale slice (any size) -> the [1, 3, 1024, 1024] float32 NCHW tensor
 * the encoder expects. Squashed to square, not letterboxed — matching the
 * verified Microsoft sample this is checked against, and simpler to invert
 * for point coordinates and the returned mask than aspect-preserving padding
 * would be. No separate mean/std normalization: the exported encoder graph
 * already does that internally (same reason the sample feeds it plain 0..1
 * RGB with nothing else applied).
 */
export function buildEncoderInput(slice: ExtractedSlice): Float32Array {
  const { width, height, gray } = slice;
  const src = new OffscreenCanvas(width, height);
  const sctx = src.getContext("2d");
  if (!sctx) throw new Error("2D canvas context unavailable");
  const img = sctx.createImageData(width, height);
  for (let i = 0; i < gray.length; i++) {
    const g = Math.round(Math.min(1, Math.max(0, gray[i])) * 255);
    const p = i * 4;
    img.data[p] = g;
    img.data[p + 1] = g;
    img.data[p + 2] = g;
    img.data[p + 3] = 255;
  }
  sctx.putImageData(img, 0, 0);

  const dst = new OffscreenCanvas(SAM_INPUT_SIZE, SAM_INPUT_SIZE);
  const dctx = dst.getContext("2d");
  if (!dctx) throw new Error("2D canvas context unavailable");
  dctx.drawImage(src, 0, 0, SAM_INPUT_SIZE, SAM_INPUT_SIZE);
  const resized = dctx.getImageData(0, 0, SAM_INPUT_SIZE, SAM_INPUT_SIZE);

  const plane = SAM_INPUT_SIZE * SAM_INPUT_SIZE;
  const out = new Float32Array(3 * plane);
  for (let i = 0; i < plane; i++) {
    const p = i * 4;
    out[i] = resized.data[p] / 255;
    out[plane + i] = resized.data[p + 1] / 255;
    out[2 * plane + i] = resized.data[p + 2] / 255;
  }
  return out;
}

/** A click/drag point in the slice's own display pixel space -> 1024-model space. */
export function toModelSpace(x: number, y: number, width: number, height: number): [number, number] {
  return [(x * SAM_INPUT_SIZE) / width, (y * SAM_INPUT_SIZE) / height];
}

/** The decoder's 1024x1024 logit mask -> a 0/1 mask back in the slice's own pixel grid. */
export function downsampleMask(mask1024: Float32Array, width: number, height: number): Uint8Array {
  const out = new Uint8Array(width * height);
  for (let y = 0; y < height; y++) {
    const my = Math.min(SAM_INPUT_SIZE - 1, Math.floor((y / height) * SAM_INPUT_SIZE));
    for (let x = 0; x < width; x++) {
      const mx = Math.min(SAM_INPUT_SIZE - 1, Math.floor((x / width) * SAM_INPUT_SIZE));
      out[y * width + x] = mask1024[my * SAM_INPUT_SIZE + mx] > 0 ? 1 : 0;
    }
  }
  return out;
}

/** The inverse of `extractSlice`'s layout: paint a display-space mask back into a 3D volume. */
export function embedMaskIntoVolume(
  dims: [number, number, number],
  orientation: SliceOrientation,
  index: number,
  width: number,
  height: number,
  maskDisplay: Uint8Array,
): Float32Array {
  const [nx, ny, nz] = dims;
  const out = new Float32Array(nx * ny * nz);
  const voxIndex = (x: number, y: number, z: number) => x + y * nx + z * nx * ny;
  for (let destRow = 0; destRow < height; destRow++) {
    const v = height - 1 - destRow;
    for (let u = 0; u < width; u++) {
      if (!maskDisplay[destRow * width + u]) continue;
      let x = 0;
      let y = 0;
      let z = 0;
      if (orientation === "axial") {
        x = u;
        y = v;
        z = index;
      } else if (orientation === "coronal") {
        x = u;
        y = index;
        z = v;
      } else {
        x = index;
        y = u;
        z = v;
      }
      out[voxIndex(x, y, z)] = 1;
    }
  }
  return out;
}
